import React, { useCallback } from "react";
import { useDispatch } from "react-redux";

import { showToast } from "../../../../../../store/actions";
import TemplateBrowser from "../../../TemplateBrowser";
import TemplateService from "../../../../../../service/template";

export default function UploadTemplate(props) {
  let {
    setOpen,
    type,
    setFields,
    dataTableRef,
    dataSources,
    setErrors,
    setOpenErrorLogPopup,
    templateDataRef,
    setTemplateName,
    fetchDSRequiredInfo,
    clearMatchingRules,
    tagsRef,
  } = props;
  const dispatch = useDispatch();

  //Getting file and folder data
  const getData = useCallback(async () => {
    const templateService = new TemplateService();
    const responseData = await templateService.getData(type);
    if (!dataSources) await fetchDSRequiredInfo();
    templateDataRef.current = responseData;
    return templateService.getAttributeTemplateOptions();
  }, [type, dataSources, fetchDSRequiredInfo, templateDataRef]);

  //Download click
  const downloadClick = async (templateId, templateName) => {
    const templateService = new TemplateService();
    templateService.download(templateId, templateName);
  };

  //Delete click
  const deleteClick = async (templateId) => {
    const templateService = new TemplateService();
    return await templateService.delete(templateId);
  };

  //Function to check template is valid of Existing Entity or not
  const checkForValidTemplate = (fileData) => {
    const errors = [];

    // Combine the errors by checking in Main(Modeling) JSON Object
    if (Object.keys(fileData).length === 0) errors.push({ field: "N/A", message: "Template is empty" });
    else if (!fileData?.fields?.length && !fileData?.tags?.length)
      errors.push({ field: "N/A", message: "Template has no attributes or tags." });
    else {
      if (fileData.fields.length) {
        fileData.fields.forEach((field) => {
          if (field.mappings?.length) {
            field.mappings.forEach((mapping) => {
              const matchedDS = dataSources.find((ds) => ds.name.toLowerCase() === mapping.dataSource.toLowerCase());

              //Proceed if mapped datasource is exists
              if (matchedDS) {
                if (matchedDS.fields?.length) {
                  const matchedField = matchedDS.fields.find(
                    (dsField) => dsField.name.toLowerCase() === mapping.field.toLowerCase()
                  );

                  //Proceed if mapped field is exists in mapped datasource
                  if (matchedField) {
                    //Check for length and type if mapped DS's field isn't already having a mapping
                    if (Object.keys(matchedField.entity_mapping).length === 0) {
                      //Push an error in array if length of mapped field's doesn't match
                      if (matchedField.length !== field.length)
                        errors.push({
                          field: field.name,
                          dataSource: matchedDS.name,
                          message: `"${field.name}" attribute's length is "${field.length}" and "${matchedDS.name}"."${matchedField.name}" attribute's length is "${matchedField.length}"`,
                        });

                      //Push an error in array if type of mapped field's doesn't match
                      if (matchedField.type !== field.type)
                        errors.push({
                          field: field.name,
                          dataSource: matchedDS.name,
                          message: `"${field.name}" attribute's type is "${field.type}" and "${matchedDS.name}"."${matchedField.name}" attribute's type is "${matchedField.type}"`,
                        });
                    }
                  }
                  //Push an error in mapped field doesn't exist in mapped datasource
                  else
                    errors.push({
                      field: field.name,
                      dataSource: matchedDS.name,
                      message: `"${mapping.field}" not found in "${matchedDS.name}" datasource attributes list`,
                    });
                }
                //Push an error in no fields exists in mapped datasource
                else {
                  errors.push({
                    field: field.name,
                    dataSource: matchedDS.name,
                    message: `No attributes found in "${matchedDS.name}" datasource`,
                  });
                }
              }
              //Push an error in mapped datasource doesn't exist
              else {
                errors.push({
                  field: field.name,
                  dataSource: mapping.dataSource,
                  message: `"${mapping.dataSource}" datasource not found`,
                });
              }
            });
          }
        });
      }
    }

    return errors;
  };

  //Upload file
  const uploadFile = async ({ uploadedFiles }) => {
    const fileData = uploadedFiles[0];
    if (fileData && fileData.name.lastIndexOf(".") > 0) {
      let fileExtension = fileData.name.substring(fileData.name.lastIndexOf(".") + 1, fileData.name.length);
      const fileSize = Math.round(fileData.size / 1024);

      //Check for JSON file validation
      if (fileExtension.toLowerCase() !== "json") {
        dispatch(
          showToast({
            message: "Please upload file having extension .json only.",
            type: "error",
          })
        );
      } else if (fileSize > 10000) {
        // file size greater than 10MB
        dispatch(
          showToast({
            message: "Please upload file having size equal or less than 10MB.",
            type: "error",
          })
        );
      } else {
        let fileName = fileData.name.substring(0, fileData.name.lastIndexOf("."));

        const checkUniqueFileName = templateDataRef.current.some((template) => {
          return template.name === fileName;
        });
        if (checkUniqueFileName)
          dispatch(showToast({ message: "Template with same name is already exist", type: "error" }));
        else {
          const errorMessage = await checkTemplateBeforeUpload(fileData);

          if (errorMessage)
            dispatch(
              showToast({
                message: errorMessage,
                type: "error",
              })
            );
          else {
            // Submit upload file data using Formdata
            const formData = new FormData();
            formData.append("file", fileData);
            formData.append("type", type);

            const templateService = new TemplateService();
            return await templateService.upload(formData);
          }
        }
      }
      return { success: false };
    }
  };

  //Check for Valid JSON String
  function IsJsonString(str) {
    try {
      const json = JSON.parse(str);
      return typeof json === "object";
    } catch (e) {
      return false;
    }
  }

  //Function to check for valid JSON object in Template file before upload and notify user about the warning message
  const checkTemplateBeforeUpload = (fileData) => {
    //Read template data and check for valid Template before upload
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        let errorMessage = "";

        if (!e.target.result) errorMessage = "Template is empty";
        else if (!IsJsonString(text)) errorMessage = "Template has not valid JSON object";
        else if (
          IsJsonString(text) &&
          (!JSON.parse(text).fields || !JSON.parse(text).fields.length || !Array.isArray(JSON.parse(text).fields))
        )
          errorMessage = "Template has no attributes";
        else {
          const templateFields = JSON.parse(text).fields;
          const notValidFieldObj = templateFields.some((field) => !field.hasOwnProperty("name"));
          if (notValidFieldObj) errorMessage = `Template attribute(s) has no "name" property`;
        }

        resolve(errorMessage);
      };
      reader.readAsText(fileData);
    });
  };

  //Load Template Data in Attribute table on click of "Load" Button
  const loadTemplateData = (templateName) => {
    let infoErrors = [];

    if (templateDataRef.current.length > 0) {
      const selectedTemplate = templateDataRef.current.find(
        (item) => item.name.toLowerCase() === templateName.toLowerCase()
      );

      if (Object.keys(selectedTemplate).length > 0) {
        //Check Whether Template has any conflicts
        infoErrors = checkForValidTemplate(selectedTemplate);

        //Set Errors State and Open Error Log Popup if there is any error
        setErrors(infoErrors);
        if (infoErrors.length) {
          setTemplateName(templateName);
          setOpenErrorLogPopup(true);
        } else {
          dataTableRef.current.resetData();
          tagsRef.current.resetData();

          setFields([]);

          selectedTemplate.fields.forEach((item) => {
            item.mappings = item.mappings.map((mapping) => {
              dataSources.forEach((ds) => {
                if (ds.name.toLowerCase() === mapping?.dataSource?.toLowerCase()) {
                  ds.fields.forEach((dsField) => {
                    let dsMapping = dsField.entity_mapping;
                    if (dsField.name.toLowerCase() === mapping?.field?.toLowerCase())
                      if (!Object.keys(dsMapping).length === 0) mapping = {};
                  });
                }
              });
              return mapping;
            });

            item.mappings = item.mappings.filter((mapping) => Object.keys(mapping).length > 0);

            if (item.mappings.length <= 1) item.mdm = {};

            if (Object.keys(item.mdm).length > 0) {
              item.mdm.priority = item.mdm.priority.filter((ds) =>
                item.mappings.some(({ dataSource }) => ds.toLowerCase() === dataSource.toLowerCase())
              );
              if (item.mdm.priority.length === 1) {
                item.mdm = {};
              }
            }

            dataTableRef.current.addRow(item);

            clearMatchingRules();
          });

          tagsRef.current.setTagsFromParent(JSON.parse(JSON.stringify(selectedTemplate?.tags || [])));

          dispatch(
            showToast({
              message: "Template loaded successfully.",
              type: "success",
            })
          );
        }
      }
      //Close Upload Template Popup
      setOpen(false);
    }
  };

  return (
    <TemplateBrowser
      getData={getData}
      uploadFile={uploadFile}
      deleteClick={deleteClick}
      downloadClick={downloadClick}
      saveClick={loadTemplateData}
      setOpenFileBrowser={setOpen}
      {...props}
    />
  );
}
